/**
 * billingView.component.tsx
 * Secured Communication
 *
 * Created by: Gabrielle Lindberg
 * Copyright (c) 2025 Secured Communications. All rights reserved.
 *
**/
import * as React from "react";

import { Button, CircularProgress, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, 
  Tooltip} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import { AccountBill, adminAccountServiceFactory } from "../services/adminAccount.service";
import { Account, BillingType, Membership } from "../models/auth";

interface MembershipSelection {
  member: Membership,
  newBillingType: BillingType,
  markedComp: boolean
}

export function BillingViewContainer(): React.ReactElement<any, any> {
  const [bills, setBills] = React.useState<Array<AccountBill>>();
  void setBills;

  React.useEffect(function () {
    adminAccountServiceFactory().getAccountBills().then(setBills);
  }, []);

  async function exportBill() {
    console.log(bills);
    const data = await adminAccountServiceFactory().getAccountBills();
    const bill1 = billToCSV(data);
    const blob = new Blob([bill1], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "AccountBillSummary.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  function billToCSV(bill: Array<AccountBill>) {
    let csv = "AccountID, Account Name, Paid Memberships, Guest Memberships, " +
      "Free Memberships, # Used Complementary Memberships\n";
    bill.forEach((entry) => {
      let accountName = entry.accountname.replace("\"", "\"\"");
      accountName = accountName.includes(",") ? `"${accountName}"` : accountName;
      const row = entry.accountID + ", " + accountName + ", " + entry.paid + ", " +
                  entry.guest + ", " + entry.free + ", " + entry.claimedSlots + "\n";
      csv = csv + row;
    });

    return csv;
  }

  return <>
    <div className="page-header attached-left-nav">
      <h3>Billing</h3>
      <Button 
        disableRipple disableTouchRipple
        aria-label="Export Bill"
        onClick={exportBill}>
          Export
      </Button>
    </div>
    <div className="account-list-container attached-left-nav">
      {bills ? <>
        <TableContainer className="billing-table">
          <Table aria-label="Billing Table" className="billing-table-inner">
            <TableHead className="billing-table-head">
              <TableRow>
                <TableCell>Account ID</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell>Paid Memberships</TableCell>
                <TableCell>Guest Memberships</TableCell>
                <TableCell>Free Memberships</TableCell>
                <TableCell># Used Complementary Memberships</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="billing-table-body">
              {bills.map((bill) => (
                <TableRow key={bill.accountID} className="billing-table-row">
                  <TableCell component="th" scope="row">{bill.accountID}</TableCell>
                  <TableCell>{bill.accountname}</TableCell>
                  <TableCell className="billing-value">{bill.paid}</TableCell>
                  <TableCell className="billing-value">{bill.guest}</TableCell>
                  <TableCell className="billing-value">{bill.free}</TableCell>
                  <TableCell className="billing-value">{bill.claimedSlots}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </> : <CircularProgress size={30} />}
    </div>
  </>
}


type BillTotals = {
  [type in BillingType]: number;
}

type BillSummary =
  BillTotals & 
  { comp: number };

export interface AccountBillSummaryComponentProps {
  account: Account,
  memberships?: MembershipSelection[],
}

export function AccountBillSummaryComponent(props: AccountBillSummaryComponentProps): React.ReactElement<any, any> {
  const [summary, setSummary] = React.useState<BillSummary>();
  const [totals, setTotals] = React.useState<BillTotals>();

  React.useEffect(function () {
    if (props.memberships) {
      aggregateBillingTypes(props.memberships);
    } else {
      adminAccountServiceFactory().getAccountMembers(props.account._id, "", 0).then(acctMembers => {
        const memberships: MembershipSelection[] = acctMembers.map(member => {
          return {
            member: member,
            newBillingType: member.billingType,
            markedComp: !!member.isComp
          }
        });
        aggregateBillingTypes(memberships);
      }, err => {
        console.error("Error fetching account members:", err);
      });
    }
  }, []);

  React.useEffect(function () {
    if (!props.memberships) { return; }
    aggregateBillingTypes(props.memberships);
  }, [props.memberships]);

  function aggregateBillingTypes(members: MembershipSelection[]) {
    const summary: BillSummary = {
      [BillingType.paid]: 0,
      [BillingType.guest]: 0,
      [BillingType.free]: 0,
      comp: 0
    }
    const totals: BillTotals = {
      [BillingType.paid]: 0,
      [BillingType.guest]: 0,
      [BillingType.free]: 0,
    }

    members.forEach(member => {
      switch(member.newBillingType) {
        case BillingType.paid: {
          totals.paid++;
          member.markedComp ? summary.comp++ : summary.paid++;
          break;
        }
        case BillingType.guest: {
          totals.guest++;
          member.markedComp ? summary.comp++ : summary.guest++;
          break;
        }
        case BillingType.free: {
          totals.free++;
          member.markedComp ? summary.comp++ : summary.free++;
          break;
        }
        default: 
          break;
      }
    });

    setSummary(summary);
    setTotals(totals);
  }

  function generateReport() {
    if (!summary || !totals) { return; }
    // show total of that type in parentheses if some were marked as complementary
    const paidStr = summary.paid === totals.paid ? summary.paid : 
                      summary.paid + " (" + totals.paid + ")";
    const guestStr = summary.guest === totals.guest ? summary.guest :
                      summary.guest + " (" + totals.guest + ")";
    const freeStr = summary.free === totals.free ? summary.free :
                      summary.free +  " (" + totals.free + ")";
    const compStr = summary.comp;

    return <>
      <table aria-label="Billing Summary Table" className="billing-summary-table">
        <thead>
          <tr className="billing-table-head">
            <th>Billing Type</th>
            <th># Memberships</th>
          </tr>
        </thead>
        <tbody>
          <tr className="billing-table-row">
            <td>Paid</td>
            <td className="billing-value">{paidStr}</td>
          </tr>
          <tr className="billing-table-row">
            <td>Guest</td>
            <td className="billing-value">{guestStr}</td>
          </tr>
          <tr className="billing-table-row">
            <td>Free</td>
            <td className="billing-value">{freeStr}</td>
          </tr>
          <tr className="billing-table-row">
            <td>Marked Complementary  <Tooltip className="info-tooltip" title={
                "Billed the same as free memberships but are specified differently"} arrow>
                <InfoIcon className="info" />
              </Tooltip>
            </td>
            <td className="billing-value">{compStr}</td>
          </tr>
        </tbody>
      </table>
      <></>
    </>
  }

  return (summary && totals) ? <>
    <div className="billing-summary" data-testid={`${props.account.accountname}-bill`}>
      {generateReport()}
    </div>
  </> : <CircularProgress size={20} />
}